(() => {
  'use strict';
  const historyKey = 'sth.search.recent.v2';
  const legacyHistoryKey = 'sth.search.recent.v1';
  const clean = value => String(value || '').replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 160);
  // K3: only complete, proven aliases in these exact department scopes.
  const mappedTerm = (term, clause) => {
    const key = term.replace(/\s+/g, ' ').toLowerCase();
    if (clause === '(product_type:"Electronics" OR product_type:"Cell Phones & Accessories" OR product_type:"Amazon Devices & Accessories" OR tag:"dept:electronics")' && ['powerbank', 'external phone battery'].includes(key)) return 'power bank';
    if (clause === '(product_type:"Home & Kitchen" OR product_type:"Tools & Home Improvement" OR product_type:"Patio, Lawn & Garden" OR product_type:"Arts, Crafts & Sewing" OR tag:"dept:home-and-kitchen")' && ['waterbottle', 'drinking bottle'].includes(key)) return 'water bottle';
    return term;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class StoreSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('#search_main');
      this.panel = form.querySelector('[data-sth-search-panel]');
      this.list = form.querySelector('#sth-search-options');
      this.status = form.querySelector('#sth-search-status');
      this.footer = form.querySelector('[data-sth-search-footer]');
      this.clearButton = form.querySelector('[data-sth-search-clear]');
      this.historyButton = form.querySelector('[data-sth-search-clear-history]');
      if (!this.input || !this.panel || !this.list || !this.footer) return;
      this.departmentSelect = form.querySelector('[data-sth-search-department]');
      const context = form.querySelector('[data-sth-departments]');
      this.departments = new Map();
      try {
        const rows = JSON.parse(context?.dataset.sthDepartments || '[]');
        if (Array.isArray(rows)) rows.forEach(row => {
          if (row && /^[a-z0-9-]+$/.test(row.handle) && typeof row.clause === 'string' && Array.isArray(row.rules)) this.departments.set(row.handle, row);
        });
      } catch (_) {}
      if (this.departmentSelect && this.departments.size) {
        const selected = context?.dataset.sthCurrentDepartment || '';
        this.departmentSelect.value = this.departments.has(selected) ? selected : '';
        this.departmentSelect.closest('.sth-search-department').hidden = false;
        this.departmentSelect.addEventListener('change', () => {
          this.cancel();
          this.input.focus();
          this.changed();
        });
      }
      this.sequence = 0;
      this.selected = -1;
      this.cache = new Map();
      this.options = [];
      this.composing = false;
      this.allowOpen = false;
      form.dataset.sthSearchReady = 'true';
      this.input.addEventListener('input', () => this.changed());
      this.input.addEventListener('search', () => this.changed());
      this.input.addEventListener('focus', () => { this.allowOpen = true; this.changed(); });
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancel(); });
      this.input.addEventListener('compositionend', () => { this.composing = false; this.changed(); });
      this.input.addEventListener('keydown', event => this.keydown(event));
      form.addEventListener('focusout', event => {
        if (event.relatedTarget && !form.contains(event.relatedTarget)) this.close();
      });
      form.addEventListener('submit', event => {
        const query = clean(this.input.value);
        if (!query) { event.preventDefault(); this.input.focus(); return; }
        this.input.value = query;
        this.remember(query);
        this.close();
      });
      form.addEventListener('formdata', event => {
        event.formData.set('q', this.scopedQuery(clean(this.input.value)));
        event.formData.set('type', 'product');
        event.formData.set('options[prefix]', 'last');
      });
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.allowOpen = true;
        this.changed();
      });
      this.historyButton?.addEventListener('click', () => {
        try { localStorage.removeItem(historyKey); localStorage.removeItem(legacyHistoryKey); } catch (_) {}
        this.input.focus();
        this.changed();
      });
      this.footer.addEventListener('click', () => this.remember(clean(this.input.value)));
      document.addEventListener('pointerdown', event => {
        if (!form.isConnected) return;
        if (!form.contains(event.target) && !event.target.closest('[aria-controls="search"]')) this.close();
      });
      document.addEventListener('click', event => {
        if (!form.isConnected) return;
        const trigger = event.target.closest('[aria-controls="search"]');
        if (trigger && !form.contains(trigger)) {
          event.preventDefault();
          this.input.focus();
          this.allowOpen = true;
          this.changed();
        }
      });
      this.clearButton.hidden = !clean(this.input.value);
    }
    department() {
      const value = this.departmentSelect?.value || '';
      return this.departments.has(value) ? value : '';
    }
    scopedQuery(query, department = this.department()) {
      const scope = this.departments.get(department);
      const term = mappedTerm(clean(query), scope?.clause);
      // Bind shopper words to product names; unqualified terms can match the whole department.
      const words = term.split(/\s+/).map(word => `title:${JSON.stringify(word)}`).join(' AND ');
      return scope && term ? `${scope.clause} AND (${words})` : term;
    }
    cacheKey(query, department = this.department()) { return `${department}\u0000${query.toLocaleLowerCase()}`; }
    searchURL(query, department = this.department()) {
      const url = new URL(this.form.action, location.origin);
      url.search = '';
      url.searchParams.set('q', this.scopedQuery(query, department));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url.href;
    }
    resourceURL(value, type) {
      try {
        const url = new URL(value, location.origin);
        if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) return null;
        if (!url.pathname.includes(type === 'product' ? '/products/' : '/collections/')) return null;
        return url.href;
      } catch (_) { return null; }
    }
    recent() {
      try {
        const values = JSON.parse(localStorage.getItem(historyKey) ?? localStorage.getItem(legacyHistoryKey) ?? '[]');
        const seen = new Set();
        if (!Array.isArray(values)) return [];
        return values.map(value => {
          const query = clean(typeof value === 'string' ? value : value?.query);
          const department = this.departments.has(value?.department) ? value.department : '';
          return { query, department };
        }).filter(entry => {
          const key = this.cacheKey(entry.query, entry.department);
          if (!entry.query || seen.has(key)) return false;
          seen.add(key);
          return true;
        }).slice(0, 5);
      } catch (_) { return []; }
    }
    remember(value, department = this.department()) {
      const query = clean(value);
      if (!query) return;
      if (!this.departments.has(department)) department = '';
      const history = this.recent().filter(item => this.cacheKey(item.query, item.department) !== this.cacheKey(query, department));
      try { localStorage.setItem(historyKey, JSON.stringify([{ query, department }, ...history].slice(0, 5))); } catch (_) {}
    }
    cancel() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence += 1;
      this.input.setAttribute('aria-busy', 'false');
    }
    close() {
      this.allowOpen = false;
      this.cancel();
      this.panel.hidden = true;
      this.form.classList.remove('sth-search-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.select(-1);
    }
    open() {
      if (!this.allowOpen || !this.form.isConnected) return;
      this.panel.hidden = false;
      this.form.classList.add('sth-search-open');
      this.input.setAttribute('aria-expanded', 'true');
    }
    changed() {
      if (this.composing) return;
      this.cancel();
      const query = clean(this.input.value);
      this.clearButton.hidden = !query;
      this.allowOpen = document.activeElement === this.input || this.form.contains(document.activeElement);
      this.footer.hidden = !query;
      this.footer.href = this.searchURL(query);
      this.footer.textContent = query ? `View all results for “${query}”` : 'View all results';
      this.historyButton.hidden = true;
      this.reset();
      if (!query) {
        const recent = this.recent();
        if (recent.length) {
          const group = this.group('Recent searches');
          recent.forEach(entry => {
            const label = this.departments.get(entry.department)?.label || 'All departments';
            this.addOption(group, `${entry.query} · ${label}`, this.searchURL(entry.query, entry.department), 'recent', entry.query, entry.department);
          });
          this.historyButton.hidden = false;
          this.announce(`${recent.length} recent searches. Use the arrow keys to choose.`);
        } else {
          const label = this.departments.get(this.department())?.label;
          this.message(label ? `Search products in ${label}.` : 'Search products across every department.');
          this.announce('Type to find products and search suggestions.');
        }
        this.open();
        return;
      }
      if (this.cache.has(this.cacheKey(query))) {
        this.render(this.cache.get(this.cacheKey(query)), query);
        return;
      }
      this.message('Searching…');
      this.announce('Searching.');
      this.open();
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.fetch(query, sequence), 180);
    }
    async fetch(query, sequence) {
      if (sequence !== this.sequence || !this.allowOpen) return;
      const controller = new AbortController();
      this.controller = controller;
      this.input.setAttribute('aria-busy', 'true');
      const endpoint = new URL(this.form.action, location.origin);
      endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + '/suggest.json';
      const department = this.department();
      const parameters = new URLSearchParams({ q: this.scopedQuery(query, department), 'resources[type]': department ? 'product' : 'query,product,collection', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last' });
      if (department) parameters.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
      endpoint.search = parameters.toString();
      const deadline = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await window.fetch(endpoint.href, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('suggestions-unavailable');
        const data = await response.json();
        if (sequence !== this.sequence || query !== clean(this.input.value) || department !== this.department() || !this.allowOpen) return;
        const results = data?.resources?.results;
        if (!results || typeof results !== 'object') throw new Error('suggestions-unavailable');
        this.cache.set(this.cacheKey(query, department), results);
        if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
        this.render(results, query);
      } catch (_) {
        if (sequence !== this.sequence || !this.allowOpen) return;
        this.reset();
        this.message('Suggestions are unavailable. Press Enter to search.');
        this.announce('Suggestions are unavailable. You can still search.');
        this.open();
      } finally {
        clearTimeout(deadline);
        if (sequence === this.sequence) this.input.setAttribute('aria-busy', 'false');
      }
    }
    reset() {
      this.list.replaceChildren();
      this.options = [];
      this.selected = -1;
      this.input.removeAttribute('aria-activedescendant');
    }
    announce(text) { if (this.status) this.status.textContent = text; }
    message(text) { this.list.append(element('p', 'sth-search-empty', text)); }
    group(label) {
      const group = element('div', 'sth-search-group');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', label);
      const title = element('p', 'sth-search-group-title', label);
      title.setAttribute('aria-hidden', 'true');
      group.append(title);
      this.list.append(group);
      return group;
    }
    addOption(group, text, url, kind, query, department = this.department()) {
      const option = element('a', `sth-search-option sth-search-option--${kind}`);
      option.href = url;
      option.id = `sth-search-option-${this.options.length}`;
      option.dataset.sthSearchOption = kind;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.tabIndex = -1;
      if (text) option.append(element('span', 'sth-search-option-text', text));
      const index = this.options.length;
      option.addEventListener('pointermove', event => { if (event.pointerType === 'mouse') this.select(index); });
      option.addEventListener('click', () => this.remember(query, department));
      this.options.push(option);
      group.append(option);
      return option;
    }
    money(value) {
      const number = Number(value);
      if (!Number.isFinite(number) || number < 0) return '';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(number);
      } catch (_) { return `$${number.toFixed(2)}`; }
    }
    render(results, query) {
      this.reset();
      const scope = this.departments.get(this.department());
      const suggestions = new Set();
      let queryGroup;
      if (scope) {
        queryGroup = this.group('Search suggestions');
        this.addOption(queryGroup, `${query} in ${scope.label}`, this.searchURL(query), 'query', query);
        suggestions.add(query.toLocaleLowerCase());
      }
      for (const item of !scope && Array.isArray(results.queries) ? results.queries : []) {
        const text = clean(item.text);
        if (!text || suggestions.has(text.toLocaleLowerCase()) || suggestions.size >= 5) continue;
        suggestions.add(text.toLocaleLowerCase());
        queryGroup ||= this.group('Search suggestions');
        this.addOption(queryGroup, text, this.searchURL(text), 'query', text);
      }
      const products = new Set();
      const productTitles = new Set();
      const productImages = new Set();
      let productGroup;
      for (const product of Array.isArray(results.products) ? results.products : []) {
        if (scope) {
          const type = String(product.type || product.product_type || '').toLocaleLowerCase();
          const tags = (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).map(tag => String(tag).trim().toLocaleLowerCase());
          if (!scope.rules.some(rule => rule.field === 'tag' ? tags.includes(String(rule.value).toLocaleLowerCase()) : rule.field === 'product_type' && type === String(rule.value).toLocaleLowerCase())) continue;
        }
        const key = String(product.id || product.url || '');
        const url = this.resourceURL(product.url, 'product');
        const titleKey = clean(product.title).toLocaleLowerCase().replace(/\s+/g, ' ');
        const source = product.featured_image?.url || product.image;
        let imageKey = '';
        try { if (source) imageKey = new URL(source, location.origin).pathname; } catch (_) {}
        if (!key || !url || !titleKey || products.has(key) || productTitles.has(titleKey) || (imageKey && productImages.has(imageKey)) || products.size >= 4) continue;
        products.add(key);
        productTitles.add(titleKey);
        if (imageKey) productImages.add(imageKey);
        productGroup ||= this.group(scope ? `Products in ${scope.label}` : 'Products');
        const option = this.addOption(productGroup, '', url, 'product', query);
        option.dataset.productId = key;
        if (source) {
          try {
            const imageURL = new URL(source, location.origin);
            if (imageURL.protocol === 'https:') {
              imageURL.searchParams.set('width', '120');
              const img = element('img', 'sth-search-product-image');
              img.src = imageURL.href; img.alt = ''; img.width = 56; img.height = 56; img.loading = 'lazy';
              option.append(img);
            }
          } catch (_) {}
        }
        const body = element('span', 'sth-search-product-body');
        body.append(element('span', 'sth-search-product-title', product.title));
        const min = this.money(product.price_min ?? product.price);
        const max = this.money(product.price_max ?? product.price);
        if (min) body.append(element('span', 'sth-search-product-price', min === max || !max ? min : `${min} – ${max}`));
        option.append(body);
      }
      const collections = new Set();
      let collectionGroup;
      for (const collection of Array.isArray(results.collections) ? results.collections : []) {
        if (scope) continue;
        const url = this.resourceURL(collection.url, 'collection');
        if (!url || !collection.title || collections.has(url) || collections.size >= 2) continue;
        collections.add(url);
        collectionGroup ||= this.group('Departments');
        this.addOption(collectionGroup, collection.title, url, 'collection', query);
      }
      if (!this.options.length) this.message('No suggestions found. Try another word or view all results.');
      this.announce(this.options.length ? `${this.options.length} suggestions. Use the arrow keys to choose.` : 'No suggestions found.');
      this.open();
    }
    select(index) {
      this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
      this.selected = index;
      const option = this.options[index];
      if (option) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else this.input.removeAttribute('aria-activedescendant');
    }
    keydown(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key === 'Tab') { this.close(); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.allowOpen) { this.allowOpen = true; this.changed(); }
        if (this.options.length) {
          const next = event.key === 'ArrowDown' ? (this.selected + 1) % this.options.length : (this.selected <= 0 ? this.options.length - 1 : this.selected - 1);
          this.select(next);
        }
      } else if (event.key === 'Enter' && !this.panel.hidden && this.selected >= 0) {
        event.preventDefault();
        this.options[this.selected]?.click();
      }
    }
  }
  const init = () => document.querySelectorAll('form[data-sth-search]:not([data-sth-search-ready])').forEach(form => new StoreSearch(form));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();
